import { isTokenValid } from '@/util'
import { createGlobalState } from '@vueuse/core'
import type { ExternalAppStatus } from '@/types'
import type {
  AddressPostalAddress,
  AgentPersonalDetailsDTO,
  IdSignatureInfo,
  SignatorySignatureReadiness,
  TodotaskTaskDTO,
} from '@deepcloud/api-deep-id'

const USER_SESSION_KEY = 'deep_id_user_session'

export interface UserSessionData {
  idToken?: string
  idTokenExpiration?: string
  strongIdToken?: string
  strongIdTokenExpiration?: string
  personUid?: string
  qualifiedGivenNames?: string
  qualifiedNames?: string
  personalData?: AgentPersonalDetailsDTO
  status?: string
  myTasks?: TodotaskTaskDTO[]
  signatureReadiness?: SignatorySignatureReadiness[]
  signatureRequests?: IdSignatureInfo[]
  emailAddress?: string
  domicileAddress?: AddressPostalAddress
  externalAppStatus?: ExternalAppStatus
}

export const useUserSession = createGlobalState(() => {
  const session = useSessionStorage<UserSessionData>(
    USER_SESSION_KEY,
    {},
    {
      serializer: {
        read: (v) => {
          try {
            return v ? (JSON.parse(v) as UserSessionData) : {}
          } catch {
            return {}
          }
        },
        write: (v) => JSON.stringify(v),
      },
    },
  )

  function mergeSession(payload: Partial<UserSessionData>) {
    session.value = { ...session.value, ...payload }
  }

  function clearSession() {
    session.value = {}
  }

  function clearStrongSession() {
    mergeSession({
      strongIdToken: undefined,
      strongIdTokenExpiration: undefined,
    })
  }

  // The weak token is the base session; the strong token is an on-demand
  // elevation for signature confirmation and never substitutes for it.
  const isAuthenticated = computed(() =>
    isTokenValid(session.value.idToken, session.value.idTokenExpiration),
  )

  const isStrongAuthenticated = computed(() =>
    isTokenValid(
      session.value.strongIdToken,
      session.value.strongIdTokenExpiration,
    ),
  )

  return {
    session,
    mergeSession,
    clearSession,
    clearStrongSession,
    isAuthenticated,
    isStrongAuthenticated,
  }
})
